import { Button, Icon, Image, NameAvatar, Typography } from '@smartgift/frontend-toolkit';
import dayjs from 'dayjs';
import { AnimatePresence, motion } from 'framer-motion';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import ContentLoader from 'react-content-loader';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { ProductLoader } from '@hero/components/skeletonLoader';
import { FLOW_CODES, SEND_GIFT_FLOW_STEPS } from '@hero/constants';
import { ROUTE_PATHS } from '@hero/routes';
import { useCreateRecognition } from '@hero/services';
import {
  useGetLLMSuggestionsGiftMessage,
  useGetLLMSuggestionsProductFilters
} from '@hero/services/ai-service/hooks';
import { useGetBranding, useGetUser } from '@hero/services/organization-service/hooks';
import {
  SEARCH_PRODUCTS_RESPONSE_TYPE,
  useSearchProductsWithSKUs
} from '@hero/services/product-service/hooks';
import { cleanObjectEmptyFields, getName } from '@hero/utils';

import { AnimatedAIIcon } from './AnimatedAIIcon';
import * as S from './style';

const STEP_ACTION = {
  searchingOccasions: 0,
  findingBestProducts: 1,
  adjustingGiftMessage: 2,
  finishAnimationSteps: 3,
  showingProducts: 4,
  showingMessage: 5,
  showingReview: 6,
  finishAnimation: 7
};

const AI_STEP = {
  search: 'search',
  results: 'results'
};

export function AIGiftAssistantTemplateResults({ setAiStep }) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();

  const [pageIndex, setPageIndex] = useState(0);
  // const [limit, setLimit] = useState(5);
  const [allProducts, setAllProducts] = useState([]);
  const [responseType, setResponseType] = useState(SEARCH_PRODUCTS_RESPONSE_TYPE.default);

  // Animation states
  const [currentStep, setCurrentStep] = useState(0);

  const { data: brandingData } = useGetBranding();
  const { mutateAsync: createRecognition, isLoading: isCreateRecognitionLoading } =
    useCreateRecognition();

  const prompt = searchParams.get('prompt');
  const { data: dataLLMSuggestionsProductFilters } = useGetLLMSuggestionsProductFilters(
    { userPrompt: prompt },
    { enabled: !!prompt }
  );

  const {
    data: dataLLMSuggestionsGiftMessage,
    isFetching: isFetchingLLMSuggestionsGiftMessage,
    refetch: refetchLLMSuggestionsGiftMessage
  } = useGetLLMSuggestionsGiftMessage({ userPrompt: prompt }, { enabled: !!prompt });

  // configs?.productList?.defaultFilterId
  const searchProductsWithSKUsFilters = useMemo(() => {
    const { minPrice, maxPrice } = dataLLMSuggestionsProductFilters?.priceRange || {};
    const categories = dataLLMSuggestionsProductFilters?.categories || [];

    if (!minPrice && !maxPrice && !categories?.length) return {};

    return {
      filters: {
        minPrice,
        maxPrice,
        categoryIds: categories.map(category => category.id)
      }
    };
  }, [dataLLMSuggestionsProductFilters]);

  const hasProductFilters =
    !!dataLLMSuggestionsProductFilters && !!Object.keys(dataLLMSuggestionsProductFilters).length;
  const { data: dataProducts, isFetching: isFetchingProducts } = useSearchProductsWithSKUs({
    params: {
      ...searchProductsWithSKUsFilters,
      pagination: {
        limit: 5,
        page: pageIndex + 1
      }
    },
    options: {
      enabled: hasProductFilters
    },
    customOptions: {
      responseType: responseType
    }
  });

  useEffect(() => {
    if (dataProducts?.docs && pageIndex === 0) {
      setAllProducts(dataProducts.docs);
    } else if (dataProducts?.docs && pageIndex > 0) {
      setAllProducts(prev => [...prev, ...dataProducts.docs]);
    }

    if (dataProducts?.responseType) {
      setResponseType(dataProducts.responseType);
    }
  }, [dataProducts?.docs, pageIndex]);

  async function animate() {
    // Step 1: Searching occasions
    setCurrentStep(STEP_ACTION.searchingOccasions);
    await new Promise(resolve => setTimeout(resolve, 1000));

    // Step 2: Finding best products
    setCurrentStep(STEP_ACTION.findingBestProducts);
    await new Promise(resolve => setTimeout(resolve, 2000));

    // Step 3: Wait for products to load, then show products
    setCurrentStep(STEP_ACTION.adjustingGiftMessage);
    await new Promise(resolve => setTimeout(resolve, 3000));

    // Step 3: Wait for products to load, then show products
    setCurrentStep(STEP_ACTION.finishAnimationSteps);
    await new Promise(resolve => setTimeout(resolve, 3000));
  }

  useEffect(() => {
    animate();
  }, []);

  async function animateAsync() {
    const hasMessage = dataLLMSuggestionsGiftMessage?.message;
    const hasProducts = allProducts.length;

    if (hasMessage && hasProducts) {
      if (currentStep === STEP_ACTION.finishAnimationSteps) {
        await new Promise(resolve => setTimeout(resolve, 1500));
        setCurrentStep(STEP_ACTION.showingProducts);
      }

      if (currentStep === STEP_ACTION.showingProducts) {
        await new Promise(resolve => setTimeout(resolve, 1500));
        setCurrentStep(STEP_ACTION.showingMessage);
      }

      if (currentStep === STEP_ACTION.showingMessage) {
        await new Promise(resolve => setTimeout(resolve, 500));
        setCurrentStep(STEP_ACTION.showingReview);
      }

      if (currentStep === STEP_ACTION.showingReview) {
        setCurrentStep(STEP_ACTION.finishAnimation);
      }
    }
  }

  useEffect(() => {
    animateAsync();
  }, [currentStep, allProducts, dataLLMSuggestionsGiftMessage]);

  useEffect(() => {}, [currentStep, dataLLMSuggestionsGiftMessage]);

  const showSteps = currentStep < STEP_ACTION.showingProducts;
  const showProducts = currentStep >= STEP_ACTION.showingProducts;
  const showMessage = currentStep >= STEP_ACTION.showingMessage;
  const showReview = currentStep >= STEP_ACTION.showingReview;
  const animationComplete = currentStep >= STEP_ACTION.showingReview;

  const { data: userData } = useGetUser();

  const AI_GIFT_ASSISTANT_STEPS = [
    t('searchingOccasions'),
    t('findingBestProducts'),
    t('adjustingGiftMessage')
  ];

  const isBestSellersActive =
    responseType === SEARCH_PRODUCTS_RESPONSE_TYPE.bestSellers ||
    dataLLMSuggestionsProductFilters?.fallbackToDefault;

  return (
    <S.AIGiftAssistantTemplateResults>
      <S.TemplatePrompt>
        <S.TemplatePromptRow>
          <NameAvatar
            name={getName({ firstName: userData?.firstName, lastName: userData?.lastName })}
          />
          <Typography color="fg" emphasis="strong">
            {prompt}
          </Typography>
        </S.TemplatePromptRow>

        <S.TemplatePromptButtonRow>
          <S.TemplatePromptRewrite
            onClick={() => {
              setAiStep(AI_STEP.search);
            }}
          >
            <Icon name="edit" size="sm" />
            {t('rewrite')}
          </S.TemplatePromptRewrite>
        </S.TemplatePromptButtonRow>
      </S.TemplatePrompt>
      <AnimatePresence>
        {showSteps && (
          <motion.div
            initial={{ opacity: 1 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.5 }}
          >
            <S.TemplateProcess>
              {AI_GIFT_ASSISTANT_STEPS.map((process, index) => {
                const isVisible = currentStep >= index;
                const nextIsVisible = currentStep >= index + 1;
                const isLoading = !nextIsVisible && currentStep >= index - 1;
                const isComplete = (!isLoading && nextIsVisible) || animationComplete;

                if (!isVisible) return null;

                return (
                  <S.TemplateProcessItem key={process + currentStep + animationComplete}>
                    <S.TemplateProcessItemLoader>
                      {isComplete ? (
                        <Icon name="success" />
                      ) : isLoading ? (
                        <div
                          style={{
                            width: '16px',
                            height: '16px',
                            border: '2px solid #ccc',
                            borderTop: '2px solid #007bff',
                            borderRadius: '50%',
                            animation: 'spin 1s linear infinite',
                            marginRight: '4px'
                          }}
                        />
                      ) : (
                        <div style={{ width: '16px', height: '16px' }} />
                      )}
                    </S.TemplateProcessItemLoader>
                    <Typography variant="body2">{process}</Typography>
                  </S.TemplateProcessItem>
                );
              })}
            </S.TemplateProcess>
          </motion.div>
        )}
      </AnimatePresence>

      <S.TemplateResultsContainer isDisplayed={showProducts}>
        <AnimatePresence>
          {showProducts && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: 'easeOut' }}
            >
              <Typography variant="body2Bold">
                {isBestSellersActive ? t('hereAreTheBestSellersText') : t('hereAreTheProducts')}:
              </Typography>
            </motion.div>
          )}
        </AnimatePresence>

        <S.TemplateResults style={{ marginTop: 12 }}>
          <AnimatePresence>
            {showProducts && (
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, ease: 'easeOut', delay: 0.2 }}
              >
                <S.TemplateResultsList>
                  {allProducts.map((p, index) => (
                    <motion.div
                      key={p.id}
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{
                        duration: 0.4,
                        ease: 'easeOut',
                        delay: 0.4 + index * 0.1
                      }}
                    >
                      <S.AIProductPreview>
                        <Image
                          src={p?.images?.[0]?.thumb}
                          alt={p?.name}
                          width={150}
                          height={150}
                          style={{
                            width: '100%',
                            height: 'auto',
                            minHeight: '134px',
                            objectFit: 'contain'
                          }}
                        />
                        <S.AIProductPreviewBody>
                          <Typography variant="caption1">{p.name}</Typography>
                        </S.AIProductPreviewBody>
                      </S.AIProductPreview>
                    </motion.div>
                  ))}
                  {isFetchingProducts && <ProductLoader count={5} />}
                </S.TemplateResultsList>
              </motion.div>
            )}
          </AnimatePresence>
          {/* {showProducts && dataProducts?.pagination?.hasNextPage && (
          <Typography
            variant="caption1"
            style={{ marginTop: 4, cursor: 'pointer' }}
            onClick={() => {
              setLimit(limit + 5);
            }}
          >
            {t('xMoreItems', { count: 5 })}
          </Typography>
        )} */}
          {showProducts && (
            <S.TemplateReSuggest>
              {dataProducts?.pagination?.hasNextPage && (
                <S.TemplateReSuggestButton
                  onClick={() => {
                    setAllProducts([]);
                    setPageIndex(p => p + 1);
                  }}
                >
                  <Icon name="refresh" size="sm" />
                  {t('reSuggest')}
                </S.TemplateReSuggestButton>
              )}
            </S.TemplateReSuggest>
          )}

          <AnimatePresence>
            {showMessage && dataLLMSuggestionsGiftMessage?.message && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, ease: 'easeOut' }}
              >
                <S.TemplateResultsSuggestion>
                  <Typography variant="body2Bold">{t('theGiftMessage')}:</Typography>
                  <S.TemplateResultsSuggestionMessage>
                    {isFetchingLLMSuggestionsGiftMessage ? (
                      <LLMSuggestionsGiftMessageLoader />
                    ) : (
                      <Typography variant="body2" style={{ fontStyle: 'italic' }}>
                        &quot;
                        {dataLLMSuggestionsGiftMessage.message}
                        &quot;
                      </Typography>
                    )}
                  </S.TemplateResultsSuggestionMessage>
                  <S.TemplateReSuggest>
                    <S.TemplateReSuggestButton onClick={refetchLLMSuggestionsGiftMessage}>
                      <Icon name="refresh" size="sm" />
                      {t('reSuggest')}
                    </S.TemplateReSuggestButton>
                  </S.TemplateReSuggest>
                </S.TemplateResultsSuggestion>
              </motion.div>
            )}
          </AnimatePresence>
        </S.TemplateResults>
      </S.TemplateResultsContainer>

      {!showReview && (
        <S.TemplateProcessAIIcon>
          <AnimatedAIIcon />
        </S.TemplateProcessAIIcon>
      )}
      <AnimatePresence>
        {showReview && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: 'easeOut' }}
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
              marginTop: '24px'
            }}
          >
            <AnimatedAIIcon animate={false} />

            <Button
              style={{ marginLeft: 'auto' }}
              disabled={isCreateRecognitionLoading}
              loading={isCreateRecognitionLoading}
              onClick={async () => {
                const response = await createRecognition({
                  name: `AI Gift Assistant (${dayjs().format('MM.DD.YY')})`,
                  settings: {
                    expiresAt: dayjs()
                      .add(2, 'week')
                      .set('hour', 23)
                      .set('minute', 59)
                      .set('second', 0)
                      .set('millisecond', 0)
                      .toISOString()
                  },
                  products: allProducts,
                  experienceSettings: cleanObjectEmptyFields({
                    branding: {
                      giftWrapperUrl: brandingData?.branding?.giftWrappers?.default,
                      logoUrl: brandingData?.branding?.logoUrl
                    },
                    messageParams: {
                      message: dataLLMSuggestionsGiftMessage?.message
                    }
                  }),
                  meta: {
                    creationStep: SEND_GIFT_FLOW_STEPS.review
                  },
                  flowCode: FLOW_CODES.ai
                });

                navigate(ROUTE_PATHS.SEND_GIFT_REVIEW(response.code));
              }}
            >
              {t('reviewAndCustomize')}
            </Button>

            <Typography variant="caption1" style={{ textAlign: 'right' }}>
              {t('clickReviewAndCustomize')}
            </Typography>
          </motion.div>
        )}
      </AnimatePresence>
    </S.AIGiftAssistantTemplateResults>
  );
}
AIGiftAssistantTemplateResults.propTypes = {
  setAiStep: PropTypes.func.isRequired
};

function LLMSuggestionsGiftMessageLoader() {
  return (
    <ContentLoader
      speed={2}
      width="100%"
      height={18}
      viewBox="0 0 100% 18"
      backgroundColor="#cfe8f7"
      foregroundColor="#ecebeb"
    >
      <rect x="0" y="0" rx="3" ry="3" width="100%" height="18" />
      <rect x="0" y="28" rx="3" ry="3" width="87" height="18" />
    </ContentLoader>
  );
}
